/* Feb-2026 · Bowker Identifier Services title-registration mimic.
 *
 * Field-for-field clone of the myidentifiers.com "Assign Your Book's
 * Title Data" form. Admins launch this from the ISBN Manager to fill
 * in the same bundle Bowker's ONIX intake expects, then hit Submit —
 * we persist the payload against the ISBN record (PUT /admin/isbn/{id})
 * and stream it into the ONIX 3.0 export.
 *
 * The Functions checkbox list is fed by /admin/isbn/bowker-options
 * (source of truth: bowker_functions.py — 130 ONIX contributor roles).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, HelpCircle, Info, Plus, Trash2, ImageIcon, Save, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';

const WORDS = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;

const emptyContributor = () => ({
  entity_type: 'individual',
  name: '',
  biography: '',
  functions: [],
});

export default function BowkerTitleEntry({ record, onClose, onSaved }) {
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState(() => hydrate(record));
  const [view, setView] = useState('short');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.isbnBowkerOptions()
      .then((d) => setOpts(d))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Could not load Bowker options.');
        setOpts({ functions: [], mediums: [], formats: {}, ebook_file_types: [],
                    title_statuses: [], target_audiences: [] });
      });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setC = (idx, k, v) => setForm((f) => ({
    ...f,
    contributors_detailed: f.contributors_detailed.map((c, i) =>
      i === idx ? { ...c, [k]: v } : c),
  }));
  const toggleFn = (idx, fn) => setForm((f) => ({
    ...f,
    contributors_detailed: f.contributors_detailed.map((c, i) => {
      if (i !== idx) return c;
      const has = (c.functions || []).includes(fn);
      return {
        ...c,
        functions: has ? c.functions.filter((x) => x !== fn) : [...(c.functions || []), fn],
      };
    }),
  }));

  const availableFormats = useMemo(() => {
    if (!opts) return [];
    return (opts.formats && form.medium) ? (opts.formats[form.medium] || []) : [];
  }, [opts, form.medium]);

  const submit = async () => {
    if (!form.title?.trim()) {
      toast.error('Book Title is required.');
      return;
    }
    if (!form.medium) { toast.error('Medium is required.'); return; }
    if (!form.format) { toast.error('Format is required.'); return; }
    if (!form.first_genre?.trim()) { toast.error('First Genre is required.'); return; }
    if (!form.title_status) { toast.error('Title Status is required.'); return; }
    if (!form.target_audience) { toast.error('Target Audience is required.'); return; }
    if (form.price_mode === 'set_amount' && !form.price_dollars) {
      toast.error('Enter a set dollar amount (or switch to Write for Info).');
      return;
    }
    setBusy(true);
    try {
      const pub = pubDate(form.pub_month, form.pub_day, form.pub_year);
      const priceCents = form.price_mode === 'set_amount'
        ? Math.round(parseFloat(form.price_dollars || '0') * 100) : null;
      const payload = {
        title:              form.title.trim(),
        subtitle:           form.subtitle || null,
        description:        form.description || null,
        medium:             form.medium,
        format:             (form.format || '').toLowerCase().split(' ')[0], // maps to legacy FORMATS
        ebook_file_type:    form.ebook_file_type || null,
        first_genre:        form.first_genre || null,
        second_genre:       form.second_genre || null,
        title_status:       form.title_status || null,
        target_audience:    form.target_audience || null,
        publisher_display:  form.publisher || null,
        publication_date:   pub || null,
        price_mode:         form.price_mode || null,
        retail_price_cents: priceCents,
        currency:           'USD',
        cover_image_url:    form.cover_image_url || null,
        contributors_detailed: (form.contributors_detailed || []).map((c) => ({
          entity_type: c.entity_type || 'individual',
          name:        (c.name || '').trim(),
          biography:   c.biography || '',
          functions:   c.functions || [],
        })).filter((c) => c.name),
        contributors: (form.contributors_detailed || [])
          .map((c) => (c.name || '').trim()).filter(Boolean),
      };
      await adminApi.updateIsbn(record.id, payload);
      toast.success('Bowker title data saved.');
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!opts) {
    return (
      <div className="flex h-full items-center justify-center p-10 text-[12px] text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading Bowker form…
      </div>
    );
  }

  const isEbook = form.medium === 'E-Book';

  return (
    <div className="bowker-mimic" data-testid="bowker-title-entry"
         style={{ fontFamily: 'Arial, Helvetica, sans-serif',
                    color: '#333', background: '#fff' }}>
      {/* Header bar — mimics Bowker's blue chrome */}
      <div style={{ background: '#00447c', color: '#fff', padding: '10px 18px',
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.5px' }}>
          Bowker <span style={{ opacity: 0.85, fontWeight: 400 }}>PUBLISHING SERVICES</span>
        </div>
        <button type="button" onClick={onClose} data-testid="bowker-close"
                  style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ padding: '18px 24px 40px', maxWidth: 780, margin: '0 auto' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#00447c', marginBottom: 6 }}>
          Assign Your Book's Title Data
        </h1>
        <div style={{ fontSize: 13, marginBottom: 20, display: 'flex',
                          alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span><strong>ISBN:</strong> {record.isbn || '—'}</span>
          <span style={{ fontSize: 12, color: '#666' }}>
            Form view&nbsp;
            <label><input type="radio" checked={view === 'short'}
              onChange={() => setView('short')} /> Short</label>&nbsp;&nbsp;
            <label><input type="radio" checked={view === 'expanded'}
              onChange={() => setView('expanded')} /> Expanded</label>
          </span>
        </div>

        {/* ── Title Information ────────────────────────────────── */}
        <BSection title="Title Information">
          <BField label="Book Title" required>
            <input type="text" value={form.title || ''} maxLength={300}
                     onChange={(e) => set('title', e.target.value)}
                     data-testid="bowker-book-title" className="bwk-input" />
          </BField>
          <div style={{ fontSize: 11, marginTop: -6, marginBottom: 12 }}>
            <em>If you need to enter Volume or Edition information, use the standard editor.</em>
          </div>
          <BField label="Subtitle">
            <input type="text" value={form.subtitle || ''} maxLength={300}
                     onChange={(e) => set('subtitle', e.target.value)}
                     data-testid="bowker-subtitle" className="bwk-input" />
          </BField>
          <BField label="Describe your book"
                   note={`(${WORDS(form.description)} of 350 words)`}>
            <textarea value={form.description || ''} rows={4}
                          onChange={(e) => set('description', e.target.value)}
                          data-testid="bowker-description" className="bwk-input"
                          placeholder="No HTML mark-up" />
          </BField>
        </BSection>

        {/* ── Medium & Format ─────────────────────────────────── */}
        <BSection title="Medium & Format">
          <BField label="Medium" required>
            <select value={form.medium || ''} data-testid="bowker-medium"
                       onChange={(e) => { set('medium', e.target.value); set('format', ''); }}
                       className="bwk-input">
              <option value="">-- Select --</option>
              {opts.mediums.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </BField>
          <BField label="Format" required>
            <select value={form.format || ''} data-testid="bowker-format"
                       onChange={(e) => set('format', e.target.value)}
                       disabled={!form.medium} className="bwk-input">
              <option value="">-- Select --</option>
              {availableFormats.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </BField>
          {isEbook && (
            <BField label="eBook File Type">
              <select value={form.ebook_file_type || ''} data-testid="bowker-ebook-type"
                         onChange={(e) => set('ebook_file_type', e.target.value)}
                         className="bwk-input">
                <option value="">-- Select --</option>
                {opts.ebook_file_types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </BField>
          )}
        </BSection>

        {/* ── Subjects & Genres ───────────────────────────────── */}
        <BSection title="Subjects & Genres">
          <BField label="First Genre" required>
            <input type="text" value={form.first_genre || ''} maxLength={120}
                     onChange={(e) => set('first_genre', e.target.value)}
                     data-testid="bowker-first-genre" className="bwk-input"
                     placeholder="FICTION_ROMANCE_CONTEMPORARY" />
          </BField>
          <BField label="Second Genre">
            <input type="text" value={form.second_genre || ''} maxLength={120}
                     onChange={(e) => set('second_genre', e.target.value)}
                     data-testid="bowker-second-genre" className="bwk-input"
                     placeholder="FICTION_ROMANCE_NEW ADULT" />
          </BField>
        </BSection>

        {/* ── Authors & Contributors ──────────────────────────── */}
        <BSection title="Authors & Contributors">
          <InfoBox>
            <div><strong>To Add a contributor:</strong> Click "Add Another Contributor" and add data in the form below. <strong>DO NOT</strong> enter the same person or group more than once.</div>
            <div><strong>To Edit a contributor:</strong> Click on the Contributor Name.</div>
            <div><strong>To Delete a contributor:</strong> Click "Delete" to the right of the name.</div>
          </InfoBox>
          {(form.contributors_detailed || []).map((c, idx) => (
            <ContributorBlock key={idx} idx={idx} c={c}
                                  functions={opts.functions}
                                  onChange={setC}
                                  onToggleFn={toggleFn}
                                  onDelete={() => set('contributors_detailed',
                                    form.contributors_detailed.filter((_, i) => i !== idx))} />
          ))}
          <button type="button" data-testid="bowker-add-contributor"
                     onClick={() => set('contributors_detailed',
                        [...(form.contributors_detailed || []), emptyContributor()])}
                     style={{ marginTop: 10, background: '#f5faff', border: '1px solid #00447c',
                                color: '#00447c', padding: '8px 14px', borderRadius: 3,
                                fontSize: 12, cursor: 'pointer', display: 'inline-flex',
                                alignItems: 'center', gap: 6 }}>
            <Plus size={12} /> Add Another Author/Contributor
          </button>
        </BSection>

        {/* ── Sales & Pricing ─────────────────────────────────── */}
        <BSection title="Sales & Pricing">
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555',
                             marginBottom: 8, letterSpacing: '0.5px' }}>UNITED STATES</div>
          <BField label="Publisher">
            <input type="text" value={form.publisher || opts.publisher || ''} maxLength={200}
                     onChange={(e) => set('publisher', e.target.value)}
                     data-testid="bowker-publisher" className="bwk-input" />
          </BField>
          <BField label="Publication Date" required>
            <div style={{ display: 'flex', gap: 6 }}>
              <select value={form.pub_month || ''} onChange={(e) => set('pub_month', e.target.value)}
                        data-testid="bowker-pub-month" className="bwk-input" style={{ flex: 1 }}>
                <option value="">MONTH</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i+1} value={String(i+1).padStart(2, '0')}>{monthName(i+1)}</option>
                ))}
              </select>
              <select value={form.pub_day || ''} onChange={(e) => set('pub_day', e.target.value)}
                        data-testid="bowker-pub-day" className="bwk-input" style={{ flex: 1 }}>
                <option value="">DAY</option>
                {Array.from({ length: 31 }, (_, i) =>
                  <option key={i+1} value={String(i+1).padStart(2, '0')}>{i+1}</option>)}
              </select>
              <select value={form.pub_year || ''} onChange={(e) => set('pub_year', e.target.value)}
                        data-testid="bowker-pub-year" className="bwk-input" style={{ flex: 1 }}>
                <option value="">YEAR</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const y = new Date().getFullYear() - 2 + i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
            </div>
          </BField>
          <BField label="Title Status" required>
            <select value={form.title_status || ''} data-testid="bowker-title-status"
                       onChange={(e) => set('title_status', e.target.value)} className="bwk-input">
              <option value="">-- Select --</option>
              {opts.title_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </BField>
          <BField label="Target Audience" required>
            <select value={form.target_audience || ''} data-testid="bowker-target-audience"
                       onChange={(e) => set('target_audience', e.target.value)} className="bwk-input">
              <option value="">-- Select --</option>
              {opts.target_audiences.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </BField>
          <BField label="Book Price" required>
            <div>
              <label style={{ marginRight: 16, fontSize: 12 }}>
                <input type="radio" checked={form.price_mode === 'set_amount'}
                          onChange={() => set('price_mode', 'set_amount')}
                          data-testid="bowker-price-set" /> Set Dollar Amount
              </label>
              <label style={{ fontSize: 12 }}>
                <input type="radio" checked={form.price_mode === 'write_for_info'}
                          onChange={() => set('price_mode', 'write_for_info')}
                          data-testid="bowker-price-wfi" /> Write for Info (No set price or free)
              </label>
            </div>
            {form.price_mode === 'set_amount' && (
              <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>$</span>
                <input type="number" step="0.01" min="0" value={form.price_dollars || ''}
                          onChange={(e) => set('price_dollars', e.target.value)}
                          data-testid="bowker-price-dollars" className="bwk-input"
                          style={{ width: 120 }} placeholder="19.99" />
              </div>
            )}
          </BField>
        </BSection>

        {/* ── Book Cover Image ────────────────────────────────── */}
        <BSection title="Book Cover Image">
          <ul style={{ fontSize: 11, color: '#555', paddingLeft: 20, marginBottom: 12 }}>
            <li>File Type: JPG only</li>
            <li>Minimum File Size: 4KB</li>
            <li>Maximum File Size: 5MB</li>
          </ul>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                             padding: 12, border: '1px dashed #ccc', background: '#fafafa' }}>
            {form.cover_image_url ? (
              <img src={form.cover_image_url} alt="cover"
                     style={{ width: 90, height: 128, objectFit: 'cover', border: '1px solid #ccc' }} />
            ) : (
              <div style={{ width: 90, height: 128, background: '#eee',
                                 display: 'flex', alignItems: 'center', justifyContent: 'center',
                                 color: '#999', fontSize: 11 }}>
                <ImageIcon size={20} /> No Image
              </div>
            )}
            <input type="url" placeholder="Paste image URL (JPG)…"
                     value={form.cover_image_url || ''}
                     onChange={(e) => set('cover_image_url', e.target.value)}
                     data-testid="bowker-cover-url" className="bwk-input" />
          </div>
        </BSection>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button type="button" onClick={submit} disabled={busy}
                     data-testid="bowker-submit"
                     style={{ background: '#00447c', color: '#fff', border: 'none',
                                fontWeight: 700, fontSize: 13, padding: '10px 26px',
                                borderRadius: 3, cursor: busy ? 'wait' : 'pointer',
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                opacity: busy ? 0.7 : 1 }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Submit
          </button>
          <button type="button" onClick={onClose} disabled={busy}
                     data-testid="bowker-cancel"
                     style={{ background: '#fff', color: '#00447c',
                                border: '1px solid #00447c', fontSize: 13,
                                padding: '10px 20px', borderRadius: 3, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
        <div style={{ fontSize: 11, color: '#666', marginTop: 12 }}>
          * Indicates required field
        </div>
      </div>

      <style>{`
        .bwk-input {
          width: 100%; padding: 6px 8px; border: 1px solid #b7c3d1;
          border-radius: 2px; font-size: 13px; font-family: inherit; background: #fff;
        }
        .bwk-input:focus { outline: none; border-color: #00447c; box-shadow: 0 0 0 2px rgba(0,68,124,0.15); }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────── sub-components

function BSection({ title, children }) {
  return (
    <section style={{ marginBottom: 26 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: '#00447c',
                          borderBottom: '2px solid #00447c', paddingBottom: 4,
                          marginBottom: 14 }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

function BField({ label, required, note, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600,
                            marginBottom: 4, color: '#333' }}>
        {label}{required && <span style={{ color: '#c00' }}> *</span>}
        <HelpCircle size={11} style={{ marginLeft: 4, color: '#00447c',
                                                 verticalAlign: 'middle', cursor: 'help' }} />
        {note && <span style={{ marginLeft: 8, fontSize: 10.5, color: '#888', fontWeight: 400 }}>{note}</span>}
      </label>
      {children}
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div style={{ background: '#eef4fb', border: '1px solid #b7c9df',
                     padding: 10, marginBottom: 14, fontSize: 11.5,
                     display: 'flex', gap: 8 }}>
      <Info size={14} style={{ color: '#00447c', flexShrink: 0, marginTop: 2 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</div>
    </div>
  );
}

function ContributorBlock({ idx, c, functions, onChange, onToggleFn, onDelete }) {
  return (
    <div style={{ border: '1px solid #d0d6dd', padding: 14, marginBottom: 14,
                     background: '#fff' }}
            data-testid={`bowker-contributor-${idx}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                        marginBottom: 10 }}>
        <strong style={{ fontSize: 13, color: '#00447c' }}>Contributor {idx + 1}</strong>
        <button type="button" onClick={onDelete}
                  data-testid={`bowker-contributor-delete-${idx}`}
                  style={{ background: 'transparent', border: 'none',
                             color: '#c00', cursor: 'pointer', fontSize: 12,
                             display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Trash2 size={12} /> Delete
        </button>
      </div>
      <InfoBox>
        Each Contributor Entity is either a person, or Organization/group. If a
        person or group holds more than one Contributor Function, please select
        as many functions as applicable. <strong>DO NOT</strong> enter the same
        person or group more than once.
      </InfoBox>
      <div style={{ marginBottom: 10 }}>
        <label style={{ marginRight: 14, fontSize: 12 }}>
          <input type="radio" checked={c.entity_type === 'individual'}
                     onChange={() => onChange(idx, 'entity_type', 'individual')}
                     data-testid={`bowker-contributor-${idx}-individual`} /> Individual
        </label>
        <label style={{ fontSize: 12 }}>
          <input type="radio" checked={c.entity_type === 'organization'}
                     onChange={() => onChange(idx, 'entity_type', 'organization')}
                     data-testid={`bowker-contributor-${idx}-organization`} /> Organization
        </label>
      </div>
      <BField label={c.entity_type === 'organization' ? 'Organization Name' : 'Name'}
                required>
        <input type="text" value={c.name || ''} maxLength={200}
                  onChange={(e) => onChange(idx, 'name', e.target.value)}
                  data-testid={`bowker-contributor-${idx}-name`} className="bwk-input" />
      </BField>
      <BField label="Biography" note={`(${WORDS(c.biography)} of 350 words)`}>
        <textarea value={c.biography || ''} rows={3}
                       onChange={(e) => onChange(idx, 'biography', e.target.value)}
                       data-testid={`bowker-contributor-${idx}-bio`}
                       className="bwk-input" placeholder="No HTML mark-up" />
      </BField>
      <div style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, display: 'block' }}>
          Function <span style={{ color: '#c00' }}>*</span>
          <span style={{ marginLeft: 6, fontSize: 10.5, color: '#666', fontWeight: 400 }}>
            (Select all that apply)
          </span>
        </label>
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd',
                          padding: 8, marginTop: 4, fontSize: 11.5,
                          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '2px 8px', background: '#fafafa' }}
                data-testid={`bowker-contributor-${idx}-functions`}>
          {functions.map((fn) => (
            <label key={fn} style={{ display: 'flex', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={(c.functions || []).includes(fn)}
                         onChange={() => onToggleFn(idx, fn)}
                         data-testid={`bowker-fn-${idx}-${slug(fn)}`} />
              {fn}
            </label>
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: '#555' }}>
          <strong>Function(s) selected:</strong>{' '}
          {(c.functions || []).length ? c.functions.join(', ') : '(none)'}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────── helpers

function slug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function monthName(m) {
  return ['January','February','March','April','May','June','July',
             'August','September','October','November','December'][m - 1];
}

function pubDate(mo, d, y) {
  if (!mo || !d || !y) return '';
  return `${y}-${mo}-${d}`;
}

function hydrate(record) {
  const iso = (record.publication_date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return {
    title:            record.title || '',
    subtitle:         record.subtitle || '',
    description:      record.description || '',
    medium:           record.medium || '',
    format:           record.format || '',
    ebook_file_type:  record.ebook_file_type || '',
    first_genre:      record.first_genre || '',
    second_genre:     record.second_genre || '',
    title_status:     record.title_status || '',
    target_audience:  record.target_audience || '',
    publisher:        record.publisher_display || '',
    pub_year:         iso ? iso[1] : '',
    pub_month:        iso ? iso[2] : '',
    pub_day:          iso ? iso[3] : '',
    price_mode:       record.price_mode
                          || (record.retail_price_cents != null ? 'set_amount' : ''),
    price_dollars:    record.retail_price_cents != null
                          ? (record.retail_price_cents / 100).toFixed(2) : '',
    cover_image_url:  record.cover_image_url || '',
    contributors_detailed: (record.contributors_detailed && record.contributors_detailed.length)
      ? record.contributors_detailed
      : ((record.contributors || []).length
          ? record.contributors.map((n) => ({
              entity_type: 'individual',
              name: n,
              biography: '',
              functions: ['Author'],
            }))
          : [emptyContributor()]),
  };
}
